import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { StyledTextField } from '@modules/ui-components';
import type { AdCreativeApiClient } from '../../services/AdCreativeApiClient';
import { AD_CREATIVE_REFRESH_HANDOFF } from '../../selectors';
import {
  CREATIVE_DECISION_REFRESH_VARIATIONS,
  buildCreativeDecisionRefreshDraftRequest,
  resolveCreativeDecisionRefreshAvailability,
  type CreativeDecisionRefreshParseResult,
  type CreativeDecisionRefreshStoppingPolicy,
  type CreativeDecisionRefreshVariation,
} from './creativeDecisionRefreshHandoff';

interface CreativeDecisionRefreshDraftPageProps {
  projectId: string;
  client: AdCreativeApiClient;
  parseResult: CreativeDecisionRefreshParseResult;
  onBack: () => void;
  onOpenBatch?: (briefId: string) => void;
}

const STOPPING_POLICIES: Array<{
  id: CreativeDecisionRefreshStoppingPolicy;
  label: string;
}> = [
  { id: 'fixed_horizon_no_peeking', label: 'Fixed horizon · no peeking' },
  { id: 'registered_sequential', label: 'Registered sequential policy' },
];

/**
 * An unpersisted review surface. Merely opening this page cannot create a
 * brief, generate an asset, or write to the connected advertising account.
 */
export function CreativeDecisionRefreshDraftPage({
  projectId,
  client,
  parseResult,
  onBack,
  onOpenBatch,
}: CreativeDecisionRefreshDraftPageProps): React.ReactElement {
  const [variation, setVariation] = useState<CreativeDecisionRefreshVariation | ''>('');
  const [minimumDetectableEffect, setMinimumDetectableEffect] = useState('');
  const [minimumDuration, setMinimumDuration] = useState('');
  const [stoppingPolicy, setStoppingPolicy] = useState<CreativeDecisionRefreshStoppingPolicy | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completedBriefId, setCompletedBriefId] = useState<string | null>(null);

  const handoff = parseResult.status === 'valid' ? parseResult.handoff : null;
  const availability = useMemo(
    () => (handoff ? resolveCreativeDecisionRefreshAvailability(handoff) : null),
    [handoff],
  );
  const mdeNumber = Number(minimumDetectableEffect);
  const durationNumber = Number(minimumDuration);
  const protocolComplete = Boolean(
    handoff
    && availability?.status === 'available'
    && variation
    && Number.isFinite(mdeNumber)
    && mdeNumber > 0
    && mdeNumber <= 100
    && Number.isInteger(durationNumber)
    && durationNumber >= 1
    && durationNumber <= 90
    && stoppingPolicy,
  );

  const submit = async (): Promise<void> => {
    if (!handoff || !variation || !stoppingPolicy || !protocolComplete) return;
    setSubmitting(true);
    setError(null);
    try {
      const request = buildCreativeDecisionRefreshDraftRequest({
        projectId,
        handoff,
        variation,
        minimumDetectableEffectPercent: mdeNumber,
        minimumDurationDays: durationNumber,
        stoppingPolicy,
      });
      const response = await client.generateBatch(request);
      if (!response.success || !response.data?.brief?.id) {
        throw new Error(response.error || 'The Segmently brief drafts could not be created.');
      }
      setCompletedBriefId(response.data.brief.id);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setSubmitting(false);
    }
  };

  if (parseResult.status !== 'valid') {
    return (
      <Stack spacing={2} data-testid={AD_CREATIVE_REFRESH_HANDOFF.page}>
        <BackButton onBack={onBack} />
        <Alert severity="error" data-testid={AD_CREATIVE_REFRESH_HANDOFF.invalid}>
          {parseResult.status === 'invalid'
            ? `This refresh draft link is invalid: ${parseResult.message}`
            : 'This page requires a Creative Decisions refresh handoff.'}
        </Alert>
      </Stack>
    );
  }
  const reviewedHandoff = parseResult.handoff;

  return (
    <Stack spacing={2.5} data-testid={AD_CREATIVE_REFRESH_HANDOFF.page}>
      <BackButton onBack={onBack} />

      <Box>
        <Typography variant="h5" sx={{ fontWeight: 750 }}>
          Review refresh experiment draft
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Review immutable source lineage and register the test protocol before Segmently creates any new brief drafts.
        </Typography>
      </Box>

      {error && (
        <Alert
          severity="error"
          data-testid={AD_CREATIVE_REFRESH_HANDOFF.error}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {completedBriefId && (
        <Alert
          severity="success"
          data-testid={AD_CREATIVE_REFRESH_HANDOFF.completed}
          action={onOpenBatch ? (
            <Button
              color="inherit"
              size="small"
              data-testid={AD_CREATIVE_REFRESH_HANDOFF.openCompleted}
              onClick={() => onOpenBatch(completedBriefId)}
            >
              Open drafts
            </Button>
          ) : undefined}
        >
          {`Segmently review drafts were created as brief ${completedBriefId}. The connected provider ad and spend were not changed.`}
        </Alert>
      )}

      <Alert severity="warning" data-testid={AD_CREATIVE_REFRESH_HANDOFF.noProviderMutation}>
        The connected-account source stays read-only. This action never edits, converts, pauses, relaunches, or
        changes spend on the provider ad. It creates new Segmently brief drafts only after you confirm below.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2 }} data-testid={AD_CREATIVE_REFRESH_HANDOFF.sourceSummary}>
        <Stack spacing={1.25}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Source decision</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
            <SourceField label="Provider" value={providerLabel(reviewedHandoff.provider)} />
            <SourceField label="Provider account" value={reviewedHandoff.providerAccountId} />
            <SourceField label="Source resource" value={reviewedHandoff.providerResourceName} />
            <SourceField label="Decision" value="Refresh due" />
            <SourceField label="As of" value={formatTimestamp(reviewedHandoff.sourceAsOf)} />
          </Stack>
          <Divider />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
            <SourceField label="Deployment" value={reviewedHandoff.deploymentKey} />
            <SourceField label="Projection" value={`${reviewedHandoff.projectionId} · ${reviewedHandoff.projectionVersion}`} />
            <SourceField label="Measurement scope" value={reviewedHandoff.scopeRef} />
            <SourceField label="Capability snapshot" value={reviewedHandoff.capabilitySnapshotId} />
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }} data-testid={AD_CREATIVE_REFRESH_HANDOFF.lineage}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.25 }}>Immutable lineage</Typography>
        <LineageGroup label="Source revisions" values={reviewedHandoff.sourceRevisionRefs} />
        <LineageGroup label="Artifact snapshots" values={reviewedHandoff.artifactSnapshotKeys} />
        <LineageGroup label="Decision metric definitions" values={reviewedHandoff.decisionMetricKeys} />
        <LineageGroup label="Evidence refs" values={reviewedHandoff.evidenceRefs} />
      </Paper>

      {availability?.status === 'unavailable' ? (
        <Alert severity="info" data-testid={AD_CREATIVE_REFRESH_HANDOFF.unavailable}>
          <strong>{availability.code}</strong>
          <br />
          {availability.message}
        </Alert>
      ) : (
        <Paper variant="outlined" sx={{ p: 2 }} data-testid={AD_CREATIVE_REFRESH_HANDOFF.protocol}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Experiment protocol</Typography>
              <Typography variant="body2" color="text.secondary">
                No MDE or duration is inferred from the decision. Choose and review both values before creating drafts.
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>1. Choose what the refresh will vary</Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                {CREATIVE_DECISION_REFRESH_VARIATIONS.map((option) => (
                  <Button
                    key={option.id}
                    variant={variation === option.id ? 'contained' : 'outlined'}
                    color={variation === option.id ? 'primary' : 'inherit'}
                    data-testid={AD_CREATIVE_REFRESH_HANDOFF.variation(option.id)}
                    onClick={() => setVariation(option.id)}
                    sx={{ flex: 1, alignItems: 'flex-start', justifyContent: 'flex-start', textAlign: 'left', py: 1.25 }}
                  >
                    <Stack spacing={0.25}>
                      <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>{option.label}</Typography>
                      <Typography component="span" variant="caption" color="inherit">{option.description}</Typography>
                    </Stack>
                  </Button>
                ))}
              </Stack>
            </Box>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <StyledTextField
                label="Minimum detectable effect (%)"
                type="number"
                value={minimumDetectableEffect}
                onChange={setMinimumDetectableEffect}
                helperText="Operator-selected relative threshold · greater than 0, at most 100"
                data-testid={AD_CREATIVE_REFRESH_HANDOFF.mde}
                fullWidth
                required
              />
              <StyledTextField
                label="Minimum duration (days)"
                type="number"
                value={minimumDuration}
                onChange={setMinimumDuration}
                helperText="Operator-selected whole number · 1 to 90 days"
                data-testid={AD_CREATIVE_REFRESH_HANDOFF.minimumDuration}
                fullWidth
                required
              />
              <StyledTextField
                select
                label="Stopping policy"
                value={stoppingPolicy}
                onChange={(value) => setStoppingPolicy(value as CreativeDecisionRefreshStoppingPolicy | '')}
                data-testid={AD_CREATIVE_REFRESH_HANDOFF.stoppingPolicy}
                fullWidth
                required
              >
                <MenuItem value="" disabled>Select a policy</MenuItem>
                {STOPPING_POLICIES.map((policy) => (
                  <MenuItem key={policy.id} value={policy.id}>{policy.label}</MenuItem>
                ))}
              </StyledTextField>
            </Stack>

            <Alert severity="info" icon={false}>
              <Stack spacing={0.5}>
                <Typography variant="caption"><strong>Target estimand:</strong> intention-to-treat contribution per assigned user</Typography>
                <Typography variant="caption"><strong>Primary metric:</strong> profit per assigned user</Typography>
                <Typography variant="caption"><strong>Early-signal status:</strong> diagnostic only — it cannot declare an economic winner</Typography>
                <Typography variant="caption">
                  <strong>Guardrails:</strong> refunds, disputes, effective cancellations, payment recovery, and complaints
                </Typography>
              </Stack>
            </Alert>

            <Button
              variant="contained"
              size="large"
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <AutorenewIcon />}
              data-testid={AD_CREATIVE_REFRESH_HANDOFF.submit}
              disabled={!protocolComplete || submitting || Boolean(completedBriefId)}
              onClick={() => void submit()}
              sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
            >
              {completedBriefId ? 'Segmently review drafts created' : 'Create Segmently brief drafts for review'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              The next screen is a Segmently brief review. No live ad and no provider launch task is created here.
            </Typography>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}

function BackButton({ onBack }: { onBack: () => void }): React.ReactElement {
  return (
    <Button
      size="small"
      startIcon={<ArrowBackIcon />}
      data-testid={AD_CREATIVE_REFRESH_HANDOFF.back}
      onClick={onBack}
      sx={{ alignSelf: 'flex-start' }}
    >
      Back to Creative decisions
    </Button>
  );
}

function SourceField({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <Box sx={{ minWidth: 180, maxWidth: 420 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{value}</Typography>
    </Box>
  );
}

function LineageGroup({ label, values }: { label: string; values: string[] }): React.ReactElement {
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography variant="caption" color="text.secondary">{`${label} (${values.length})`}</Typography>
      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
        {values.length === 0 ? (
          <Typography variant="body2" color="text.secondary">None supplied</Typography>
        ) : values.slice(0, 12).map((value) => (
          <Chip key={value} size="small" variant="outlined" label={value} sx={{ maxWidth: 360 }} />
        ))}
        {values.length > 12 && <Chip size="small" label={`+${values.length - 12} more`} />}
      </Stack>
    </Box>
  );
}

function providerLabel(provider: 'meta' | 'google_ads' | 'tiktok_ads'): string {
  if (provider === 'google_ads') return 'Google Ads';
  if (provider === 'tiktok_ads') return 'TikTok Ads';
  return 'Meta Ads';
}

function formatTimestamp(value: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
  } catch {
    return value;
  }
}
